import "./CompanyClients.css";
import { usePermissions } from "./PermissionContext";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const emptyCustomer = {
  document_type: "CC",
  document_number: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  is_active: true,
};

const emptyLoyalty = {
  movement_type: "earn",
  points: "",
  concept: "",
  reference_type: "manual",
  reference_id: "",
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-CO");
};

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("es-CO");
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

function CompanyClients() {
  const { can } = usePermissions();

  const canManageCustomers = can("customers.manage");

  const [customers, setCustomers] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [summary, setSummary] = useState(null);

  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);

  const [customerForm, setCustomerForm] = useState(emptyCustomer);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const [loading, setLoading] = useState(true);
  const [savingCustomer, setSavingCustomer] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerStatement, setCustomerStatement] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const portfolioRequestId = useRef(0);

  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const [loyaltyForm, setLoyaltyForm] = useState(emptyLoyalty);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [savingLoyalty, setSavingLoyalty] = useState(false);

  const loadCustomers = useCallback(async () => {
    const response = await axios.get(`${API_URL}/customers/me`, {
      headers: getHeaders(),
    });

    setCustomers(response.data);
  }, []);

  const loadPortfolio = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/customers/me/portfolio`,
      {
        headers: getHeaders(),
      }
    );

    setPortfolio(response.data);
  }, []);

  const loadSummary = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/customers/me/summary`,
      {
        headers: getHeaders(),
      }
    );

    setSummary(response.data);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadCustomers(),
        loadPortfolio(),
        loadSummary(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [loadCustomers, loadPortfolio, loadSummary]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCustomerChange = (event) => {
    const { name, value, type, checked } = event.target;

    setCustomerForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetCustomerForm = () => {
    setCustomerForm(emptyCustomer);
    setEditingCustomer(null);
  };

  const handleCustomerSubmit = async (event) => {
    event.preventDefault();
    if (!canManageCustomers) {
      toast.error(
        "No tienes permiso para crear o editar clientes."
      );
      return;
    }

    if (
      !customerForm.document_number.trim() ||
      !customerForm.name.trim()
    ) {
      toast.error(
        "El documento y el nombre del cliente son obligatorios."
      );
      return;
    }

    setSavingCustomer(true);

    try {
      const payload = {
        ...customerForm,
        document_number: customerForm.document_number.trim(),
        name: customerForm.name.trim(),
      };

      if (editingCustomer) {
        await axios.put(
          `${API_URL}/customers/me/${editingCustomer.id}`,
          payload,
          { headers: getHeaders() }
        );

        toast.success("Cliente actualizado correctamente.");
      } else {
        await axios.post(
          `${API_URL}/customers/me`,
          payload,
          { headers: getHeaders() }
        );

        toast.success("Cliente registrado correctamente.");
      }

      resetCustomerForm();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleEditCustomer = (customer) => {
    if (!canManageCustomers) {
      toast.error(
        "No tienes permiso para editar clientes."
      );
      return;
    }
    setEditingCustomer(customer);

    setCustomerForm({
      document_type: customer.document_type || "CC",
      document_number: customer.document_number || "",
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      notes: customer.notes || "",
      is_active: Boolean(customer.is_active),
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleToggleCustomer = async (customer) => {
    try {
      if (!canManageCustomers) {
        toast.error(
          "No tienes permiso para cambiar el estado del cliente."
        );
        return;
      }
      await axios.patch(

        `${API_URL}/customers/me/${customer.id}/toggle-active`,
        {},
        { headers: getHeaders() }
      );

      toast.success(
        customer.is_active
          ? "Cliente inactivado."
          : "Cliente activado."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openPortfolioModal = async (customer) => {
    const requestId = ++portfolioRequestId.current;

    setSelectedCustomer(customer);
    setCustomerInvoices([]);
    setCustomerStatement(null);
    setLoadingStatement(true);
    setShowPortfolioModal(true);
    setLoadingInvoices(true);

    const [invoicesResult, statementResult] = await Promise.allSettled([
      axios.get(`${API_URL}/customers/me/${customer.id}/invoices`, {
        headers: getHeaders(),
      }),
      axios.get(`${API_URL}/customers/me/${customer.id}/statement`, {
        headers: getHeaders(),
      }),
    ]);

    // Ignora respuestas de una ventana que ya se cerró o cambió de cliente.
    if (portfolioRequestId.current !== requestId) {
      return;
    }

    if (invoicesResult.status === "fulfilled") {
      setCustomerInvoices(invoicesResult.value.data.items || []);
    } else {
      toast.error(getErrorMessage(invoicesResult.reason));
    }

    if (statementResult.status === "fulfilled") {
      setCustomerStatement(statementResult.value.data);
    } else {
      toast.error(getErrorMessage(statementResult.reason));
    }

    setLoadingInvoices(false);
    setLoadingStatement(false);
  };

  const closePortfolioModal = () => {
    portfolioRequestId.current += 1;
    setShowPortfolioModal(false);
    setSelectedCustomer(null);
    setCustomerInvoices([]);
    setCustomerStatement(null);
    setLoadingInvoices(false);
    setLoadingStatement(false);
  };

  const loadLoyaltyHistory = async (customerId) => {
    setLoadingLoyalty(true);

    try {
      const response = await axios.get(
        `${API_URL}/customers/me/${customerId}/loyalty`,
        {
          headers: getHeaders(),
        }
      );

      setLoyaltyHistory(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingLoyalty(false);
    }
  };

  const openLoyaltyModal = async (customer) => {
    setSelectedCustomer(customer);
    setLoyaltyForm(emptyLoyalty);
    setShowLoyaltyModal(true);

    await loadLoyaltyHistory(customer.id);
  };

  const closeLoyaltyModal = () => {
    setShowLoyaltyModal(false);
    setSelectedCustomer(null);
    setLoyaltyHistory([]);
    setLoyaltyForm(emptyLoyalty);
  };

  const handleLoyaltyChange = (event) => {
    const { name, value } = event.target;

    setLoyaltyForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleLoyaltySubmit = async (event) => {
    event.preventDefault();
    if (!canManageCustomers) {
      toast.error(
        "No tienes permiso para modificar la fidelización."
      );
      return;
    }

    if (
      !loyaltyForm.points ||
      Number(loyaltyForm.points) <= 0 ||
      !loyaltyForm.concept.trim()
    ) {
      toast.error(
        "Indica los puntos y el concepto del movimiento."
      );
      return;
    }

    setSavingLoyalty(true);

    try {
      await axios.post(
        `${API_URL}/customers/me/${selectedCustomer.id}/loyalty`,
        {
          movement_type: loyaltyForm.movement_type,
          points: Number(loyaltyForm.points),
          concept: loyaltyForm.concept.trim(),
          reference_type: loyaltyForm.reference_type || null,
          reference_id: loyaltyForm.reference_id
            ? Number(loyaltyForm.reference_id)
            : null,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success("Movimiento de puntos registrado.");

      setLoyaltyForm(emptyLoyalty);

      await loadLoyaltyHistory(selectedCustomer.id);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingLoyalty(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return customers.filter((customer) => {
      if (onlyActive && !customer.is_active) return false;
      if (!term) return true;

      return (
        customer.name?.toLowerCase().includes(term) ||
        customer.document_number?.toLowerCase().includes(term) ||
        customer.phone?.toLowerCase().includes(term) ||
        customer.email?.toLowerCase().includes(term)
      );
    });
  }, [customers, search, onlyActive]);

  const portfolioByCustomer = useMemo(() => {
    return portfolio.reduce((result, item) => {
      result[item.customer_id] = item;
      return result;
    }, {});
  }, [portfolio]);

  const getInvoiceStatusLabel = (invoiceStatus) => {
    const labels = {
      issued: "Emitida",
      partial_paid: "Pago parcial",
      paid: "Pagada",
      overdue: "Vencida",
      cancelled: "Anulada",
    };

    return labels[invoiceStatus] || invoiceStatus;
  };

  const getLoyaltyLabel = (movementType) => {
    const labels = {
      earn: "Acumulación",
      redeem: "Canje",
      adjustment: "Ajuste",
    };

    return labels[movementType] || movementType;
  };

  return (
    <div className="company-clients">
      <section className="clients-heading">
        <div>
          <p className="clients-kicker">Relación comercial</p>
          <h1>Clientes</h1>
          <p>
            Actualiza datos, consulta cartera y administra fidelización.
          </p>
        </div>

        <button
          type="button"
          className="btn-client-refresh"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      <section className="client-summary-grid">
        <article className="client-summary-card">
          <span>Clientes registrados</span>
          <strong>{summary?.customers_count ?? 0}</strong>
        </article>

        <article className="client-summary-card success">
          <span>Clientes activos</span>
          <strong>{summary?.active_customers_count ?? 0}</strong>
        </article>

        <article className="client-summary-card pending">
          <span>Cartera pendiente</span>
          <strong>{money(summary?.total_pending)}</strong>
        </article>

        <article className="client-summary-card overdue">
          <span>Cartera vencida</span>
          <strong>{money(summary?.total_overdue)}</strong>
        </article>

        <article className="client-summary-card loyalty">
          <span>Puntos entregados</span>
          <strong>{summary?.total_loyalty_points ?? 0}</strong>
        </article>
      </section>

      <section className="clients-card client-form-card">
        <div className="client-card-heading">
          <div>
            <h2>
              {editingCustomer
                ? "Editar cliente"
                : "Registrar cliente"}
            </h2>
            <p>Documento y nombre son campos obligatorios.</p>
          </div>

          {editingCustomer && (
            <button
              type="button"
              className="btn-client-cancel"
              onClick={resetCustomerForm}
            >
              Cancelar edición
            </button>
          )}
        </div>
        {canManageCustomers ? (
          <form
            className="client-form"
            onSubmit={handleCustomerSubmit}
          >
            <div className="client-form-grid">
              <label>
                Tipo de documento
                <select
                  name="document_type"
                  value={customerForm.document_type}
                  onChange={handleCustomerChange}
                >
                  <option value="CC">Cédula de ciudadanía</option>
                  <option value="CE">Cédula de extranjería</option>
                  <option value="NIT">NIT</option>
                  <option value="TI">Tarjeta de identidad</option>
                  <option value="PASSPORT">Pasaporte</option>
                  <option value="OTHER">Otro</option>
                </select>
              </label>

              <label>
                Número de documento
                <input
                  type="text"
                  name="document_number"
                  value={customerForm.document_number}
                  onChange={handleCustomerChange}
                  placeholder="Documento del cliente"
                  required
                />
              </label>

              <label>
                Nombre completo o razón social
                <input
                  type="text"
                  name="name"
                  value={customerForm.name}
                  onChange={handleCustomerChange}
                  placeholder="Nombre del cliente"
                  required
                />
              </label>

              <label>
                Correo electrónico
                <input
                  type="email"
                  name="email"
                  value={customerForm.email}
                  onChange={handleCustomerChange}
                  placeholder="correo@ejemplo.com"
                />
              </label>

              <label>
                Teléfono
                <input
                  type="text"
                  name="phone"
                  value={customerForm.phone}
                  onChange={handleCustomerChange}
                  placeholder="Número telefónico"
                />
              </label>

              <label>
                Dirección
                <input
                  type="text"
                  name="address"
                  value={customerForm.address}
                  onChange={handleCustomerChange}
                  placeholder="Dirección del cliente"
                />
              </label>
            </div>

            <label className="client-notes">
              Observaciones
              <textarea
                name="notes"
                value={customerForm.notes}
                onChange={handleCustomerChange}
                placeholder="Información comercial adicional"
                rows="3"
              />
            </label>

            <label className="client-check">
              <input
                type="checkbox"
                name="is_active"
                checked={customerForm.is_active}
                onChange={handleCustomerChange}
              />
              Cliente activo
            </label>

            <button
              type="submit"
              className="btn-client-save"
              disabled={savingCustomer}
            >
              {savingCustomer
                ? "Guardando..."
                : editingCustomer
                  ? "Actualizar cliente"
                  : "Crear cliente"}
            </button>
          </form>
        ) : (
          <div className="client-readonly-message">
            No tienes permiso para crear o editar clientes.
          </div>
        )}
      </section>

      <section className="clients-card client-list-card">
        <div className="client-card-heading client-list-heading">
          <div>
            <h2>Listado de clientes</h2>
            <p>Consulta datos, cartera y puntos disponibles.</p>
          </div>

          <div className="client-filters">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar nombre, documento, correo o teléfono"
            />

            <label className="client-filter-check">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(event) => setOnlyActive(event.target.checked)}
              />
              Solo activos
            </label>
          </div>
        </div>

        <div className="client-table-wrapper">
          <table className="client-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Documento</th>
                <th>Contacto</th>
                <th>Cartera</th>
                <th>Puntos</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="client-empty">
                    Cargando clientes...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="client-empty">
                    No hay clientes para mostrar.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => {
                  const customerPortfolio =
                    portfolioByCustomer[customer.id];

                  return (
                    <tr key={customer.id}>
                      <td>
                        <strong>{customer.name}</strong>
                        {customer.email && (
                          <small>{customer.email}</small>
                        )}
                      </td>

                      <td>
                        {customer.document_type}{" "}
                        {customer.document_number}
                      </td>

                      <td>
                        {customer.phone || "Sin teléfono"}
                      </td>

                      <td>
                        <strong className="client-money">
                          {money(
                            customerPortfolio?.pending_balance
                          )}
                        </strong>

                        {Number(
                          customerPortfolio?.overdue_balance || 0
                        ) > 0 && (
                            <small className="client-overdue-text">
                              Vencido:{" "}
                              {money(
                                customerPortfolio.overdue_balance
                              )}
                            </small>
                          )}
                      </td>

                      <td>
                        <span className="client-points">
                          {customer.loyalty_points || 0}
                        </span>
                      </td>

                      <td>
                        <span
                          className={
                            customer.is_active
                              ? "client-status active"
                              : "client-status inactive"
                          }
                        >
                          {customer.is_active
                            ? "Activo"
                            : "Inactivo"}
                        </span>
                      </td>

                      <td>
                        <div className="client-actions">
                          {canManageCustomers && (
                            <button
                              type="button"
                              className="btn-client-edit"
                              onClick={() =>
                                handleEditCustomer(customer)
                              }
                            >
                              Editar
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-client-portfolio"
                            onClick={() =>
                              openPortfolioModal(customer)
                            }
                          >
                            Cartera
                          </button>
                          {canManageCustomers && (
                            <button
                              type="button"
                              className="btn-client-loyalty"
                              onClick={() =>
                                openLoyaltyModal(customer)
                              }
                            >
                              Puntos
                            </button>
                          )}
                          {canManageCustomers && (
                            <button
                              type="button"
                              className={
                                customer.is_active
                                  ? "btn-client-disable"
                                  : "btn-client-enable"
                              }
                              onClick={() =>
                                handleToggleCustomer(customer)
                              }
                            >
                              {customer.is_active
                                ? "Inactivar"
                                : "Activar"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showPortfolioModal && selectedCustomer && (
        <div className="client-modal-backdrop">
          <section className="client-modal">
            <div className="client-modal-header">
              <div>
                <p className="clients-kicker">Cartera</p>
                <h2>{selectedCustomer.name}</h2>
                <span>
                  {selectedCustomer.document_type}{" "}
                  {selectedCustomer.document_number}
                </span>
              </div>

              <button
                type="button"
                className="client-modal-close"
                onClick={closePortfolioModal}
              >
                ×
              </button>
            </div>

            <p className="client-modal-help">
              Los pagos de estas facturas se registran desde el módulo
              Facturación.
            </p>

            <div className="client-table-wrapper">
              <table className="client-invoice-table">
                <thead>
                  <tr>
                    <th>Factura</th>
                    <th>Emisión</th>
                    <th>Vencimiento</th>
                    <th>Total</th>
                    <th>Pagado</th>
                    <th>Saldo</th>
                    <th>Estado</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingInvoices ? (
                    <tr>
                      <td colSpan="7" className="client-empty">
                        Cargando facturas...
                      </td>
                    </tr>
                  ) : customerInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="client-empty">
                        El cliente no tiene facturas asociadas.
                      </td>
                    </tr>
                  ) : (
                    customerInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.number}</td>
                        <td>{formatDate(invoice.issue_date)}</td>
                        <td>
                          {invoice.due_date
                            ? formatDate(invoice.due_date)
                            : "Sin vencimiento"}
                        </td>
                        <td className="client-money">
                          {money(invoice.total)}
                        </td>
                        <td className="client-money paid">
                          {money(invoice.paid_amount)}
                        </td>
                        <td
                          className={
                            invoice.is_overdue
                              ? "client-money overdue"
                              : "client-money"
                          }
                        >
                          {money(invoice.pending_amount)}
                        </td>
                        <td>
                          <span
                            className={`client-invoice-status ${invoice.status}`}
                          >
                            {getInvoiceStatusLabel(invoice.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <section className="client-statement">
              <h3>Estado de cuenta</h3>

              {loadingStatement ? (
                <p className="client-modal-help">
                  Cargando movimientos de cartera...
                </p>
              ) : !customerStatement ? (
                <p className="client-modal-help">
                  No fue posible cargar el estado de cuenta.
                </p>
              ) : (
                <>
                  <p className="client-modal-help">
                    Cargos: {money(customerStatement.total_charges)}
                    {" · "}
                    Abonos: {money(customerStatement.total_payments)}
                    {" · "}
                    Saldo: {money(customerStatement.closing_balance)}
                  </p>

                  <div className="client-table-wrapper">
                    <table className="client-invoice-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Movimiento</th>
                          <th>Factura</th>
                          <th>Cargo</th>
                          <th>Abono</th>
                          <th>Saldo</th>
                        </tr>
                      </thead>

                      <tbody>
                        {!customerStatement.items?.length ? (
                          <tr>
                            <td colSpan="6" className="client-empty">
                              El cliente no tiene movimientos de cartera.
                            </td>
                          </tr>
                        ) : (
                          customerStatement.items.map((item, index) => (
                            <tr
                              key={`${item.type}:${item.invoice_id}:${index}`}
                            >
                              <td>{formatDate(item.date)}</td>
                              <td>{item.description}</td>
                              <td>{item.invoice_number}</td>
                              <td className="client-money">
                                {Number(item.charge) > 0
                                  ? money(item.charge)
                                  : "—"}
                              </td>
                              <td className="client-money paid">
                                {Number(item.payment) > 0
                                  ? money(item.payment)
                                  : "—"}
                              </td>
                              <td className="client-money">
                                {money(item.balance)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </section>
        </div>
      )}

      {showLoyaltyModal && selectedCustomer && (
        <div className="client-modal-backdrop">
          <section className="client-modal">
            <div className="client-modal-header">
              <div>
                <p className="clients-kicker">Fidelización</p>
                <h2>{selectedCustomer.name}</h2>
                <span>
                  Puntos actuales:{" "}
                  <strong>{selectedCustomer.loyalty_points || 0}</strong>
                </span>
              </div>

              <button
                type="button"
                className="client-modal-close"
                onClick={closeLoyaltyModal}
              >
                ×
              </button>
            </div>
            {canManageCustomers ? (
              <form
                className="client-loyalty-form"
                onSubmit={handleLoyaltySubmit}
              >
                <h3>Registrar movimiento de puntos</h3>

                <div className="client-form-grid">
                  <label>
                    Tipo de movimiento
                    <select
                      name="movement_type"
                      value={loyaltyForm.movement_type}
                      onChange={handleLoyaltyChange}
                    >
                      <option value="earn">Acumular puntos</option>
                      <option value="redeem">Canjear puntos</option>
                      <option value="adjustment">Ajuste manual</option>
                    </select>
                  </label>

                  <label>
                    Cantidad de puntos
                    <input
                      type="number"
                      name="points"
                      min="1"
                      value={loyaltyForm.points}
                      onChange={handleLoyaltyChange}
                      placeholder="0"
                      required
                    />
                  </label>

                  <label>
                    Tipo de referencia
                    <input
                      type="text"
                      name="reference_type"
                      value={loyaltyForm.reference_type}
                      onChange={handleLoyaltyChange}
                      placeholder="manual, venta, promoción..."
                    />
                  </label>

                  <label>
                    ID de referencia
                    <input
                      type="number"
                      name="reference_id"
                      value={loyaltyForm.reference_id}
                      onChange={handleLoyaltyChange}
                      placeholder="Opcional"
                    />
                  </label>
                </div>

                <label className="client-notes">
                  Concepto
                  <textarea
                    name="concept"
                    value={loyaltyForm.concept}
                    onChange={handleLoyaltyChange}
                    placeholder="Ejemplo: Puntos por compra especial"
                    rows="2"
                    required
                  />
                </label>

                <button
                  type="submit"
                  className="btn-client-save"
                  disabled={savingLoyalty}
                >
                  {savingLoyalty
                    ? "Guardando..."
                    : "Registrar movimiento"}
                </button>
              </form>
            ) : (
              <div className="client-readonly-message">
                No tienes permiso para modificar la fidelización.
              </div>
            )}
            <section className="client-loyalty-history">
              <h3>Historial de puntos</h3>

              {loadingLoyalty ? (
                <p className="client-empty">
                  Cargando historial...
                </p>
              ) : loyaltyHistory.length === 0 ? (
                <p className="client-empty">
                  No hay movimientos de puntos registrados.
                </p>
              ) : (
                loyaltyHistory.map((movement) => (
                  <article
                    className="client-loyalty-item"
                    key={movement.id}
                  >
                    <div>
                      <strong>
                        {getLoyaltyLabel(movement.movement_type)}
                      </strong>
                      <span>{movement.concept}</span>
                      <small>{formatDateTime(movement.created_at)}</small>
                    </div>

                    <span
                      className={
                        movement.movement_type === "redeem"
                          ? "client-points movement-redeem"
                          : "client-points movement-earn"
                      }
                    >
                      {movement.movement_type === "redeem" ? "-" : "+"}
                      {movement.points}
                    </span>
                  </article>
                ))
              )}
            </section>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyClients;
